import { useCallback, useEffect, useRef } from 'react';
import { useOnInView } from 'react-intersection-observer';
import logger from '@/utils/client-logger';

export interface SubscribeExposureGate {
  updateVisibility: (isVisible: boolean) => boolean;
  handleRouteChange: () => void;
  canExpose: () => boolean;
  markExposed: () => void;
}

export interface UseSubscribeExposureTrackingOptions {
  callback: () => void;
  pathname: string;
  threshold?: number;
  minDuration?: number;
}

export interface UseSubscribeExposureTrackingReturn {
  ref: (node?: Element | null) => void;
}

export function createSubscribeExposureGate(): SubscribeExposureGate {
  let isVisible = false;
  let isExposed = false;
  let requiresFreshEntry = false;

  return {
    updateVisibility(nextVisible) {
      isVisible = nextVisible;

      if (!nextVisible) {
        requiresFreshEntry = false;
        return false;
      }

      return !isExposed && !requiresFreshEntry;
    },
    handleRouteChange() {
      isExposed = false;
      requiresFreshEntry = isVisible;
    },
    canExpose() {
      return isVisible && !isExposed && !requiresFreshEntry;
    },
    markExposed() {
      isExposed = true;
    },
  };
}

export function useSubscribeExposureTracking(
  options: UseSubscribeExposureTrackingOptions,
): UseSubscribeExposureTrackingReturn {
  const { callback, pathname, threshold = 0.8, minDuration = 500 } = options;

  const gateRef = useRef(createSubscribeExposureGate());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    clearTimer();
    gateRef.current.handleRouteChange();
  }, [clearTimer, pathname]);

  const handleIntersection = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry) => {
      const ratio = entry?.intersectionRatio ?? 0;
      const isVisible = inView && ratio >= threshold;
      const shouldStartTimer = gateRef.current.updateVisibility(isVisible);

      if (!shouldStartTimer) {
        clearTimer();
        return;
      }

      clearTimer();
      timerRef.current = setTimeout(() => {
        if (!gateRef.current.canExpose()) {
          timerRef.current = null;
          return;
        }

        try {
          callbackRef.current();
          gateRef.current.markExposed();
        } catch (error) {
          logger.log('[useSubscribeExposureTracking] Callback error:', error);
        } finally {
          timerRef.current = null;
        }
      }, minDuration);
    },
    [clearTimer, minDuration, threshold],
  );

  const inViewRef = useOnInView(handleIntersection, {
    threshold,
    triggerOnce: false,
  });

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clearTimer]);

  return { ref: inViewRef };
}
